// Party frames, alliance frames, invite and request prompts, and the Party Finder window.
// Everything here is a picture of what the server said: actions are requests, answered with a fresh party_state.
import { factionById } from '../../../shared/factions.js';
import { NOTE_MAX, PARTY_MAX, type PartyOp, type PartyView } from '../../../shared/party.js';
import type { FriendView, ServerMsg } from '../../../shared/protocol.js';
import { repLine } from './social.js';
import { jobDef, ROLE_COLOR, ROLE_NAME, ROLE_OF, type JobId } from '../../../shared/jobs.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e;
}
function button(text: string, cls: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', cls, text); b.type = 'button'; b.addEventListener('click', e => { e.stopPropagation(); onClick(); }); return b;
}

type State = Extract<ServerMsg, { type: 'party_state' }>;
type Offer = Extract<ServerMsg, { type: 'party_offer' }>;
type Finder = Extract<ServerMsg, { type: 'finder' }>;

export class PartyUI {
  myChar = 0;
  state: State = { type: 'party_state', party: null, alliance: null, seeking: null };
  onOp: (op: PartyOp) => void = () => {};
  onWhisper: (name: string) => void = () => {};
  /** Raise a fallen party member (you're a Cleric who knows Raise). */
  onRaise: (char: number) => void = () => {};
  canRaise: () => boolean = () => false;
  /** Auto-follow: who you're following (by name), and start/stop. */
  following: () => string | null = () => null;
  onFollow: (name: string | null) => void = () => {};
  private frames = $('party'); private offersBox = $('offers'); private win = $('finder');
  private menu: HTMLElement | null = null;
  private tab: 'friends' | 'seekers' | 'parties' | 'blocked' = 'friends';
  /** Player cards, mutes, blocks and reports (the social module answers these). */
  onProfile: (name: string, x: number, y: number) => void = () => {};
  onSocial: (m: { op: 'block' | 'unblock' | 'blocks'; name?: string; account?: number }) => void = () => {};
  onReport: (name: string) => void = () => {};
  isMuted: (name: string) => boolean = () => false;
  onMute: (name: string, on: boolean) => void = () => {};
  private blocked: { account: number; name: string }[] | null = null;
  setBlocked(list: { account: number; name: string }[]): void { this.blocked = list; if (this.finderOpen && this.tab === 'blocked') this.renderList(); }
  friends = new Map<number, FriendView>();
  myLayer = 1;
  onFriend: (m: { op: 'add'; name: string } | { op: 'remove'; char: number } | { op: 'answer'; offer: number; yes: boolean } | { op: 'join' | 'visit'; char: number }) => void = () => {};
  private list: Finder | null = null;
  private timer = 0;

  constructor() {
    $('partyBtn').addEventListener('click', () => this.toggleFinder());
    $('finderClose').addEventListener('click', () => this.toggleFinder(false));
    $('finderRefresh').addEventListener('click', () => this.refresh());
    for (const t of this.win.querySelectorAll<HTMLButtonElement>('[data-tab]')) t.addEventListener('click', () => { this.tab = t.dataset.tab as 'friends' | 'seekers' | 'parties' | 'blocked'; if (this.tab === 'blocked') this.onSocial({ op: 'blocks' }); this.renderList(); });
    addEventListener('pointerdown', e => { if (this.menu && !this.menu.contains(e.target as Node)) this.closeMenu(); });
    addEventListener('keydown', e => { if (e.key === 'Escape') { if (this.menu) this.closeMenu(); else if (this.finderOpen) this.toggleFinder(false); } });
  }

  get party(): PartyView | null { return this.state.party; }
  get isLeader(): boolean { return this.party?.leader === this.myChar; }
  get finderOpen(): boolean { return !this.win.hidden; }

  reset(): void {
    this.state = { type: 'party_state', party: null, alliance: null, seeking: null };
    this.offersBox.replaceChildren(); this.toggleFinder(false); this.render();
  }

  /** Lowercased names of everyone in your party and alliance. */
  memberNames(): Set<string> {
    const out = new Set<string>();
    for (const p of this.state.alliance ?? (this.party ? [this.party] : [])) for (const m of p.members) out.add(m.name.toLowerCase());
    return out;
  }

  /** A name typed in a command, matched against your party and alliance. */
  /** The party leader's name, when you're in a party and aren't the leader. */
  leaderName(): string | null {
    const p = this.state.party; if (!p || p.leader === this.myChar) return null;
    return p.members.find(m => m.char === p.leader)?.name ?? null;
  }
  memberByName(name: string): number | null {
    const k = name.toLowerCase();
    for (const p of this.state.alliance ?? (this.party ? [this.party] : [])) for (const m of p.members) if (m.name.toLowerCase() === k) return m.char;
    return null;
  }

  // ---------- server messages ----------
  setState(m: State): void { this.state = m; this.render(); if (this.finderOpen) this.renderMine(); }

  offer(o: Offer): void {
    const card = el('div', 'offer'); card.dataset.offer = String(o.offer);
    const pip = el('span', 'pip'); pip.style.background = factionById(o.faction)?.color ?? '#999';
    const text = el('div', 'offer-text');
    const who = el('b', '', o.name);
    text.append(pip, who, document.createTextNode(
      o.kind === 'party' ? (o.size > 1 ? ` invites you to their party (${o.size}/${PARTY_MAX}).` : ' invites you to a party.')
        : o.kind === 'alliance' ? `'s party invites yours to an alliance.`
          : ' asks to join your party.'));
    const answer = (yes: boolean) => { this.onOp({ op: 'answer', offer: o.offer, yes }); card.remove(); };
    const acts = el('div', 'offer-acts');
    acts.append(button(o.kind === 'request' ? 'Accept' : 'Join', 'btn primary', () => answer(true)), button('Decline', 'btn', () => answer(false)));
    const bar = el('i', 'offer-bar'); bar.style.animationDuration = o.secs + 's';
    card.append(text, acts, bar);
    this.offersBox.append(card);
    while (this.offersBox.children.length > 4) this.offersBox.firstChild!.remove();
  }
  offerGone(id: number): void { this.offersBox.querySelector(`[data-offer="${id}"]`)?.remove(); }

  setFinder(f: Finder): void { this.list = f; this.renderList(); }

  // ---------- friends ----------
  setFriends(list: FriendView[]): void { this.friends = new Map(list.map(f => [f.char, f])); if (this.finderOpen) this.renderList(); }
  friendUpdate(f: FriendView): void { this.friends.set(f.char, f); if (this.finderOpen) this.renderList(); }
  friendGone(char: number): void { this.friends.delete(char); if (this.finderOpen) this.renderList(); }
  friendOffer(o: Extract<ServerMsg, { type: 'friend_offer' }>): void {
    const card = el('div', 'offer'); card.dataset.offer = 'f' + o.offer;
    const pip = el('span', 'pip'); pip.style.background = factionById(o.faction)?.color ?? '#999';
    const text = el('div', 'offer-text'); text.append(pip, el('b', '', o.name), document.createTextNode(' wants to be friends.'));
    const answer = (yes: boolean) => { this.onFriend({ op: 'answer', offer: o.offer, yes }); card.remove(); };
    const acts = el('div', 'offer-acts'); acts.append(button('Accept', 'btn primary', () => answer(true)), button('Not now', 'btn', () => answer(false)));
    const bar = el('i', 'offer-bar'); bar.style.animationDuration = '60s';
    card.append(text, acts, bar); this.offersBox.append(card);
    setTimeout(() => card.remove(), 60_000);
  }
  /** Lowercased names of friends who are online (they're always drawn clearly). */
  friendNames(): Set<string> { const out = new Set<string>(); for (const f of this.friends.values()) if (f.online) out.add(f.name.toLowerCase()); return out; }

  // ---------- party frames ----------
  private render(): void {
    this.closeMenu();
    const p = this.party, f = this.frames;
    f.replaceChildren(); f.hidden = !p;
    if (!p) return;
    const head = el('div', 'pf-head');
    head.append(el('span', 'pf-title', `Party ${p.members.length}/${PARTY_MAX}`));
    if (p.recruit !== null) head.append(el('span', 'pf-tag', 'Recruiting'));
    f.append(head);
    { // loot rules: the leader picks, everyone sees
      const L = p.loot ?? { common: 'round', rare: 'roll' }, row = el('div', 'pf-loot'), lead = p.leader === this.myChar;
      const CN: Record<string, string> = { own: 'everyone finds their own', round: 'round robin' }, RN: Record<string, string> = { roll: 'roll (Need/Greed)', round: 'round robin', leader: 'leader hands out' };
      row.append(el('span', '', '🎁 Loot:'));
      if (lead) {
        const pick = (opts: Record<string, string>, cur: string, fn: (v: string) => void, title: string) => { const s = el('select'); s.title = title; for (const [k, v] of Object.entries(opts)) { const o = el('option', '', v); o.value = k; o.selected = k === cur; s.append(o); } s.addEventListener('change', () => fn(s.value)); return s; };
        row.append(el('span', '', 'common'), pick(CN, L.common, v => this.onOp({ op: 'loot', common: v as 'own' | 'round' }), 'Materials, potions, food'), el('span', '', 'gear'), pick(RN, L.rare, v => this.onOp({ op: 'loot', rare: v as 'roll' | 'round' | 'leader' }), 'Uncommon, rare and legendary gear'));
      } else row.append(el('span', '', `common: ${CN[L.common]} · gear: ${RN[L.rare]}`));
      f.append(row);
    }
    const myZone = p.members.find(m => m.char === this.myChar)?.zone;
    for (const m of p.members) {
      const row = el('button', 'pf-row' + (m.char === this.myChar ? ' me' : '') + (m.online ? '' : ' off')); row.type = 'button';
      const dot = el('span', 'pf-dot'); dot.title = m.online ? 'Online' : m.offlineMins != null ? 'Offline: keeps their place for 10 minutes' : 'Reconnecting';
      row.append(dot, el('span', 'pf-name', m.name));
      const role = m.job ? ROLE_OF[m.job as JobId] : null;
      if (role) { const pip = el('span', 'pf-role'); pip.style.background = ROLE_COLOR[role]; pip.title = ROLE_NAME[role]; row.insertBefore(pip, row.lastChild); }
      if (m.job) { const j = jobDef(m.job), t = el('span', 'pf-job', `${j.name.slice(0, 2)}${m.lv ?? ''}`); t.style.background = j.color; t.title = `${j.name} level ${m.lv}`; row.append(t); }
      if (m.fallen) row.append(el('span', 'pf-down', 'down'));
      if (m.autoPotion) { const c = el('span', 'pf-pref', `💊${m.autoPotion}%`); c.title = `Drinks a potion by themselves below ${m.autoPotion}% health`; row.append(c); }
      if (m.autoRaise) { const c = el('span', 'pf-pref', '✨'); c.title = 'Accepts a Raise without being asked'; row.append(c); }
      if (m.rep) row.title = [repLine(m.rep)].filter(Boolean).join('');
      if (m.offlineMins != null) row.append(el('span', 'pf-zone', `offline ${m.offlineMins ? m.offlineMins + 'm' : ''}`.trim()));
      if (m.char === p.leader) { const c = el('span', 'pf-lead', '★'); c.title = 'Party leader'; row.append(c); }
      if (m.zone && m.zone !== myZone) row.append(el('span', 'pf-zone', m.zone));
      row.addEventListener('click', () => this.openMenu(row, m.char, m.name, p));
      f.append(row);
    }
    const others = (this.state.alliance ?? []).filter(x => x.id !== p.id);
    if (others.length) {
      const allianceLeader = this.state.alliance![0];
      f.append(el('div', 'pf-head pf-ally', 'Alliance'));
      for (const x of others) {
        const box = el('div', 'pf-other');
        const lead = x.members.find(m => m.char === x.leader);
        const top = el('div', 'pf-other-head', `${lead?.name ?? '?'}'s party · ${x.members.length}`);
        if (x === allianceLeader) top.append(el('span', 'pf-lead', ' ★'));
        if (allianceLeader.id === p.id && this.isLeader) top.append(button('Remove', 'pf-mini', () => this.onOp({ op: 'akick', party: x.id })));
        box.append(top);
        const names = el('div', 'pf-other-names');
        for (const m of x.members) { const n = el('span', m.online ? '' : 'off', m.name); n.addEventListener('click', () => this.onWhisper(m.name)); names.append(n); }
        box.append(names); f.append(box);
      }
    }
  }

  private openMenu(anchor: HTMLElement, char: number, name: string, p: PartyView): void {
    this.closeMenu();
    const m = el('div', 'pf-menu'), leader = this.isLeader;
    if (char === this.myChar) {
      m.append(button('Leave party', '', () => { this.onOp({ op: 'leave' }); this.closeMenu(); }));
      if (leader && this.state.alliance) m.append(button('Leave alliance', '', () => { this.onOp({ op: 'aleave' }); this.closeMenu(); }));
      if (leader) m.append(button(p.recruit === null ? 'List in Party Finder' : 'Stop recruiting', '', () => { this.closeMenu(); if (p.recruit === null) this.toggleFinder(true); else this.onOp({ op: 'recruit', on: false }); }));
    } else {
      const on = this.following() === name;
      m.append(button(on ? 'Stop following' : 'Follow', '', () => { this.closeMenu(); this.onFollow(on ? null : name); }));
      const who = p.members.find(x => x.char === char);
      if (who?.fallen && this.canRaise()) m.append(button('Raise', '', () => { this.closeMenu(); this.onRaise(char); }));
      m.append(button('Whisper', '', () => { this.closeMenu(); this.onWhisper(name); }));
      m.append(button('Profile', '', () => { const r = anchor.getBoundingClientRect(); this.closeMenu(); this.onProfile(name, r.right + 6, r.top); }));
      if (![...this.friends.values()].some(f => f.char === char)) m.append(button('Add friend', '', () => { this.closeMenu(); this.onFriend({ op: 'add', name }); }));
      if (leader) {
        m.append(button('Make leader', '', () => { this.onOp({ op: 'promote', char }); this.closeMenu(); }));
        m.append(button('Remove from party', 'danger', () => { this.onOp({ op: 'kick', char }); this.closeMenu(); }));
      }
      const muted = this.isMuted(name);
      m.append(button(muted ? 'Unmute' : 'Mute', '', () => { this.onMute(name, !muted); this.closeMenu(); }));
      m.append(button('Block', '', () => { this.onSocial({ op: 'block', name }); this.closeMenu(); }));
      m.append(button('Report…', 'danger', () => { this.closeMenu(); this.onReport(name); }));
    }
    const r = anchor.getBoundingClientRect();
    m.style.left = Math.min(r.right + 6, innerWidth - 170) + 'px'; m.style.top = r.top + 'px';
    document.body.append(m); this.menu = m;
  }
  private closeMenu(): void { this.menu?.remove(); this.menu = null; }

  // ---------- the finder ----------
  toggleFinder(force?: boolean): void {
    const open = force ?? this.win.hidden;
    this.win.hidden = !open;
    clearInterval(this.timer);
    if (open) { this.renderMine(); this.refresh(); this.timer = window.setInterval(() => this.refresh(), 5000); }
  }
  private refresh(): void { this.onOp({ op: 'finder' }); }

  /** Your own listing: the seeking flag when solo, the recruiting note when you lead a party. */
  private renderMine(): void {
    const box = $('finderMine'), p = this.party;
    box.replaceChildren();
    const noteInput = (value: string, ph: string) => { const i = el('input'); i.maxLength = NOTE_MAX; i.value = value; i.placeholder = ph; i.setAttribute('aria-label', 'Note'); return i; };
    if (!p) {
      const on = this.state.seeking !== null;
      box.append(el('div', 'fm-title', on ? "You're looking for a party" : 'Looking for a party?'));
      const i = noteInput(this.state.seeking ?? '', 'A short note: what you want to do');
      const row = el('div', 'fm-row');
      row.append(i, button(on ? 'Update' : 'Raise flag', 'btn primary', () => this.onOp({ op: 'seek', on: true, note: i.value })));
      if (on) row.append(button('Lower', 'btn', () => this.onOp({ op: 'seek', on: false })));
      box.append(row);
      box.append(el('div', 'fm-sub', 'Players in your faction will see you here and can invite you. You can also start your own party and recruit:'));
      box.append(button('Start a party and recruit', 'btn fm-wide', () => this.onOp({ op: 'recruit', on: true, note: i.value })));
      return;
    }
    if (!this.isLeader) {
      const lead = p.members.find(m => m.char === p.leader)?.name ?? '';
      box.append(el('div', 'fm-title', `You're in ${lead}'s party`), el('div', 'fm-sub', 'The party leader invites people and lists the party here.'));
      return;
    }
    const on = p.recruit !== null, full = p.members.length >= PARTY_MAX;
    box.append(el('div', 'fm-title', full ? 'Your party is full' : on ? 'Your party is listed' : 'Recruit for your party'));
    if (full) return;
    const i = noteInput(p.recruit ?? '', 'What are you looking for? (e.g. "1 more for the mines")');
    const row = el('div', 'fm-row');
    row.append(i, button(on ? 'Update' : 'List party', 'btn primary', () => this.onOp({ op: 'recruit', on: true, note: i.value })));
    if (on) row.append(button('Unlist', 'btn', () => this.onOp({ op: 'recruit', on: false })));
    box.append(row);
  }

  private renderList(): void {
    const f = this.list, box = $('finderList');
    const online = [...this.friends.values()].filter(x => x.online).length;
    for (const t of this.win.querySelectorAll<HTMLButtonElement>('[data-tab]')) {
      const id = t.dataset.tab, n = id === 'friends' ? online : id === 'blocked' ? (this.blocked?.length ?? 0) : f ? (id === 'seekers' ? f.seekers.length : f.parties.length) : 0;
      t.setAttribute('aria-selected', String(id === this.tab));
      t.textContent = (id === 'friends' ? 'Friends' : id === 'seekers' ? 'Looking for party' : id === 'blocked' ? 'Blocked' : 'Parties recruiting') + (id === 'blocked' && !n ? '' : ` (${n})`);
    }
    box.replaceChildren();
    $('finderMine').hidden = this.tab === 'friends' || this.tab === 'blocked';
    if (this.tab === 'friends') return this.renderFriends(box);
    if (this.tab === 'blocked') {
      box.append(el('p', 'fr-note', "Blocked players can't whisper, invite or friend you, and neither of you sees the other's chat. Click a name in chat for their card, or type /block name."));
      if (!this.blocked) { box.append(el('div', 'empty', 'Loading…')); return; }
      if (!this.blocked.length) box.append(el('div', 'empty', "You haven't blocked anyone."));
      for (const b of this.blocked) { const r = this.row(b.name, 0, '', ''); r.append(button('Unblock', 'btn fr-btn', () => this.onSocial({ op: 'unblock', account: b.account }))); box.append(r); }
      return;
    }
    if (!f) { box.append(el('div', 'empty', 'Loading…')); return; }
    const p = this.party, canInvite = !p || (this.isLeader && p.members.length < PARTY_MAX), solo = !p;
    if (this.tab === 'seekers') {
      const rows = f.seekers.filter(s => s.char !== this.myChar);
      if (!rows.length) box.append(el('div', 'empty', 'No one is looking right now. Raise your own flag above!'));
      for (const s of rows) {
        const r = this.row(s.name, s.faction, s.note, [s.job ? `${jobDef(s.job).name} ${s.lv}` : '', s.zone, s.mins ? `${s.mins} min` : '', repLine(s.rep)].filter(Boolean).join(' · '));
        if (canInvite) r.append(button('Invite', 'btn primary fr-btn', () => { this.onOp({ op: 'invite', char: s.char }); }));
        box.append(r);
      }
    } else {
      const rows = f.parties.filter(x => x.leader !== this.myChar);
      if (!rows.length) box.append(el('div', 'empty', 'No parties are recruiting. Start one above!'));
      for (const x of rows) {
        const r = this.row(`${x.name}'s party`, x.faction, x.note, [x.zone, `${x.size}/${PARTY_MAX}`, repLine(x.rep)].filter(Boolean).join(' · '));
        if (solo) r.append(button('Ask to join', 'btn primary fr-btn', () => this.onOp({ op: 'request', char: x.leader })));
        box.append(r);
      }
    }
  }

  private renderFriends(box: HTMLElement): void {
    const add = el('div', 'fm-row'), i = el('input'); i.maxLength = 16; i.placeholder = 'Add a friend by name'; i.setAttribute('aria-label', 'Friend name');
    const send = () => { const n = i.value.trim(); if (n) { this.onFriend({ op: 'add', name: n }); i.value = ''; } };
    i.addEventListener('keydown', e => { if (e.key === 'Enter') send(); });
    add.append(i, button('Add', 'btn primary', send));
    box.append(add);
    const list = [...this.friends.values()].sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name));
    if (!list.length) box.append(el('div', 'empty', 'No friends yet. Add someone you had fun with! Friends log in near each other and can hop onto each other\'s layer.'));
    for (const fr of list) {
      const r = this.row(fr.name, fr.faction, '', fr.online ? `${fr.zone}${fr.layer > 1 || fr.layer !== this.myLayer ? ` · layer ${fr.layer}` : ''}` : 'Offline');
      r.classList.toggle('off', !fr.online);
      if (fr.online) {
        r.append(button('Whisper', 'btn fr-btn', () => this.onWhisper(fr.name)));
        if (fr.home) r.append(button('Visit home', 'btn primary fr-btn', () => this.onFriend({ op: 'visit', char: fr.char })));
        else if (fr.layer !== this.myLayer) r.append(button('Join layer', 'btn primary fr-btn', () => this.onFriend({ op: 'join', char: fr.char })));
        if (!this.party || this.isLeader) r.append(button('Invite', 'btn fr-btn', () => this.onOp({ op: 'invite', char: fr.char })));
      }
      const rm = button('×', 'x fr-x', () => { if (rm.dataset.sure) this.onFriend({ op: 'remove', char: fr.char }); else { rm.dataset.sure = '1'; rm.textContent = 'Remove?'; rm.classList.add('sure'); } });
      rm.title = 'Remove friend'; r.append(rm);
      box.append(r);
    }
  }

  private row(name: string, faction: number, note: string, sub: string): HTMLElement {
    const r = el('div', 'fr'), main = el('div', 'fr-main'), top = el('div', 'fr-name');
    const pip = el('span', 'pip'); pip.style.background = factionById(faction)?.color ?? '#999';
    top.append(pip, document.createTextNode(name));
    main.append(top);
    if (note) main.append(el('div', 'fr-note', note));
    main.append(el('div', 'fr-sub', sub));
    r.append(main); return r;
  }
}
